import { useEffect, useState } from "react";
import { api, formatApiError } from "@/lib/api";
import { logError } from "@/lib/log";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Download, Trash2, Loader2, TrendingUp, Mail } from "lucide-react";
import { toast } from "sonner";
import AdminOptOutSync from "@/components/AdminOptOutSync";
import AdminFounders from "@/components/AdminFounders";

function AcquisitionTable({ data }) {
  if (!data) return <Loader2 className="w-5 h-5 animate-spin text-primary" />;
  return (
    <div className="bg-card border border-border rounded-2xl p-5" data-testid="admin-acquisition">
      <p className="font-display font-semibold mb-1 flex items-center gap-2"><TrendingUp className="w-4 h-4 text-accent" /> Sign-ups & revenue by source</p>
      <p className="text-xs text-muted-foreground mb-4">Captured from UTM tags and referrers on first visit. Use links like <code className="text-[11px]">futuregenservices.com/?utm_source=linkedin&amp;utm_medium=social&amp;utm_campaign=launch</code>.</p>
      {data.sources.length === 0 ? <p className="text-sm text-muted-foreground">No attributed sign-ups yet.</p> : (
        <table className="w-full text-sm">
          <thead><tr className="text-left text-xs text-muted-foreground"><th className="pb-2">Source</th><th className="pb-2 text-right">Sign-ups</th><th className="pb-2 text-right">Revenue</th></tr></thead>
          <tbody>
            {data.sources.map((r) => (
              <tr key={r.source} className="border-t border-border" data-testid={`acq-row-${r.source.replace(/[^a-z0-9]+/gi, "-").toLowerCase()}`}>
                <td className="py-2 font-medium">{r.source}</td><td className="py-2 text-right">{r.signups}</td><td className="py-2 text-right">£{r.revenue.toLocaleString("en-GB", { minimumFractionDigits: 2 })}</td>
              </tr>
            ))}
          </tbody>
        </table>
      )}
      {data.campaigns.length > 0 && (
        <div className="mt-4 flex flex-wrap gap-2">{data.campaigns.map((c) => <Badge key={c.campaign} variant="secondary">{c.campaign} · {c.signups}</Badge>)}</div>
      )}
    </div>
  );
}

export default function AdminGrowth() {
  const [acq, setAcq] = useState(null);
  const [subs, setSubs] = useState([]);
  const load = () => {
    api.get("/admin/acquisition").then((r) => setAcq(r.data)).catch((e) => logError("acquisition", e));
    api.get("/admin/newsletter").then((r) => setSubs(r.data)).catch((e) => logError("newsletter", e));
  };
  useEffect(load, []);

  const remove = async (id) => {
    try { await api.delete(`/admin/newsletter/${id}`); toast.success("Subscriber removed"); load(); }
    catch (err) { toast.error(formatApiError(err.response?.data?.detail) || "Could not remove"); }
  };
  const exportCsv = async (path, filename) => {
    const res = await api.get(path, { responseType: "blob" });
    const url = URL.createObjectURL(res.data);
    const a = document.createElement("a"); a.href = url; a.download = filename; a.click(); URL.revokeObjectURL(url);
  };

  return (
    <div className="space-y-6">
      <AdminFounders />
      <AcquisitionTable data={acq} />
      <div className="bg-card border border-border rounded-2xl p-5" data-testid="admin-newsletter">
        <div className="flex items-center justify-between mb-4 gap-3 flex-wrap">
          <div className="font-display font-semibold flex items-center gap-2"><Mail className="w-4 h-4 text-accent" /> Newsletter subscribers <Badge variant="secondary" data-testid="newsletter-count">{subs.filter((s) => s.active).length} active</Badge></div>
          <div className="flex gap-2 flex-wrap">
            <Button size="sm" variant="outline" onClick={() => exportCsv("/admin/newsletter.csv", "newsletter-subscribers.csv")} data-testid="newsletter-export"><Download className="w-4 h-4 mr-1.5" /> Export CSV</Button>
            <Button size="sm" variant="outline" onClick={() => exportCsv("/admin/opt-outs.csv", "email-opt-outs.csv")} data-testid="opt-outs-export"><Download className="w-4 h-4 mr-1.5" /> Export opt-outs</Button>
          </div>
        </div>
        {subs.length === 0 ? <p className="text-sm text-muted-foreground">No subscribers yet — the sign-up form is on the guidance and sector pages.</p> : (
          <ul className="divide-y divide-border">
            {subs.map((s) => (
              <li key={s.id} className="py-2.5 flex items-center justify-between gap-3 text-sm" data-testid={`newsletter-row-${s.id}`}>
                <div className="min-w-0"><p className="font-medium truncate">{s.email}</p><p className="text-xs text-muted-foreground">{s.regulator} · {s.source || "site"} · {new Date(s.created_at).toLocaleDateString("en-GB")}{!s.active && " · unsubscribed"}</p></div>
                <Button size="icon" variant="ghost" onClick={() => remove(s.id)} data-testid={`newsletter-delete-${s.id}`}><Trash2 className="w-4 h-4" /></Button>
              </li>
            ))}
          </ul>
        )}
      </div>
      <AdminOptOutSync />
    </div>
  );
}
